import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lightning } from "@phosphor-icons/react";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : form;
      const { data } = await api.post(url, payload);
      setUser(data);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="app-shell overflow-y-auto flex relative">
      <div className="pointer-events-none absolute inset-0 opacity-40"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1567095751004-aa51a2690368?auto=format&fit=crop&w=1600&q=60)", backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
      <div className="hidden lg:flex flex-col justify-between w-[42%] border-r border-white/[0.07] p-12 glass-panel relative z-10">
        <div className="flex items-center gap-2">
          <Lightning size={26} weight="fill" className="text-primary" />
          <span className="font-heading font-bold text-xl tracking-tight">LifeOS</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
            Вся ваша жизнь<br />
            <span className="text-primary">в одной системе</span>
          </h1>
          <p className="text-muted-foreground mt-4 text-base max-w-sm">
            Привычки, календарь, задачи, бюджет и данные фитнес-трекера — в едином тёмном дашборде.
          </p>
        </div>
        <div className="text-xs text-muted-foreground uppercase tracking-widest">
          Привычки · Календарь · Планнер · Бюджет · Whoop
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-sm fade-in glass-card p-8">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Lightning size={24} weight="fill" className="text-primary" />
            <span className="font-heading font-bold text-lg">LifeOS</span>
          </div>
          <h2 className="font-heading text-2xl font-semibold mb-1">
            {mode === "login" ? "С возвращением" : "Создать аккаунт"}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {mode === "login" ? "Войдите, чтобы продолжить" : "Начните управлять своей жизнью"}
          </p>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Имя</Label>
                <Input id="name" data-testid="auth-name-input" value={form.name} onChange={set("name")} required placeholder="Иван" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="auth-email-input" value={form.email} onChange={set("email")} required placeholder="you@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Пароль</Label>
              <Input id="password" type="password" data-testid="auth-password-input" value={form.password} onChange={set("password")} required placeholder="••••••" />
            </div>
            {error && (
              <div className="text-sm text-destructive" data-testid="auth-error">{error}</div>
            )}
            <Button type="submit" className="w-full active:scale-95 transition-transform" disabled={busy} data-testid="auth-submit-button">
              {busy ? "Подождите…" : mode === "login" ? "Войти" : "Зарегистрироваться"}
            </Button>
          </form>
          <button
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            data-testid="auth-switch-mode"
            className="mt-5 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            {mode === "login" ? "Нет аккаунта? Зарегистрироваться" : "Уже есть аккаунт? Войти"}
          </button>
        </div>
      </div>
    </div>
  );
}
